/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';
import { DeepLinkingResponse } from '@app/app/page-modules/lti/models/lti-course.model';
import { LtiLaunchSurface } from '@app/app/page-modules/lti/models/lti-experience.model';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { LtiAuthService } from '@app/app/user-auth/services/lti-auth.service';
import { environment } from '@app/environments/environment';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';

const LTI_COURSE_URL = Location.joinWithSlash(environment.apiRootUrl || '', '/lti/course');

/** Query param marking a launch of one assignment as course content — see `isAssignmentContentLaunch`. */
export const ASSIGNMENT_LAUNCH_PARAM = 'assignmentLaunch';

// Holds what kind of LTI launch opened the frameless course-setup subtree (dashboard → marking →
// assignment editor), since internal `navigateByUrl` calls drop query params: the deep-link
// `requestId`, and whether a single assignment was launched directly as course content.
@Injectable({ providedIn: 'root' })
export class LtiAssignmentLinkService {
  private readonly client = inject(RestClientService);
  private readonly ltiAuthService = inject(LtiAuthService);

  readonly requestId = signal<string | null>(null);
  readonly isLtiDeeplinkMode = computed<boolean>(() => this.requestId() !== null);

  private readonly assignmentContentLaunch = signal(false);
  readonly isAssignmentContentLaunch = this.assignmentContentLaunch.asReadonly();

  /**
   * Which landing the teacher crossed over from, so the experience switcher can offer the way back.
   * Held here for the same reason as `requestId`: the switcher lives on the dashboard, and walking
   * out to the assignment list and back drops every query param on the way.
   */
  private readonly launchSurface = signal<LtiLaunchSurface | null>(null);
  readonly originLaunchSurface = this.launchSurface.asReadonly();

  /** The LMS course playlist uid, which the classic course-tool landing reads straight off the URL. */
  private readonly launchCourseUid = signal<string | null>(null);
  readonly originCourseUid = this.launchCourseUid.asReadonly();

  captureRequestId(id: string | null): void {
    this.requestId.set(id);
  }

  captureAssignmentContentLaunch(value: boolean): void {
    this.assignmentContentLaunch.set(value);
  }

  /** Ignores anything unrecognised, so a hand-edited URL cannot point the way back at nothing. */
  captureLaunchSurface(value: string | null): void {
    this.launchSurface.set(value === 'deeplink' || value === 'courseTool' ? value : null);
  }

  captureLaunchCourseUid(value: string | null): void {
    this.launchCourseUid.set(value);
  }

  linkAssignment(courseId: string, assignmentId: string): Observable<DeepLinkingResponse> {
    const requestId = this.requestId();
    if (!requestId) {
      // Flow the guard through the observable so the caller's error handler runs (a synchronous
      // `throw` would escape the subscribe and leave the trigger's disabled state stuck).
      return throwError(() => new Error('Not in LTI deep-link mode'));
    }
    return this.client.post<DeepLinkingResponse>(`${LTI_COURSE_URL}/assignments`, { assignments: [assignmentId] }, { requestId }).pipe(
      map(({ body }) => {
        // Without a token + returnUrl we cannot hand off to the LMS. Surface it as an error
        // (→ error toast + re-enabled button) instead of committing teardown and submitting a
        // broken form that would silently strand the instructor.
        if (!body?.token || !body?.returnUrl) {
          throw new Error('Malformed LTI deep-linking response');
        }
        return body;
      }),
      tap((response) => {
        // The deep-link interaction ends here: clear the persisted request id and POST the signed
        // content item back to the LMS. `form.submit()` unloads the page, so we intentionally do NOT
        // clear the in-memory `requestId` signal — doing so would trigger a redundant assignments
        // reload and flip every card to "In course" for the instant before navigation.
        this.ltiAuthService.removeLtiRequestId();
        this.submitDeepLinkingResponse(response);
      }),
    );
  }

  private submitDeepLinkingResponse(response: DeepLinkingResponse): void {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = response.returnUrl;
    form.style.display = 'none';
    const input = document.createElement('input');
    input.name = 'JWT';
    input.value = response.token;
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
  }
}
